import flatMapDeep from 'lodash/flatMapDeep';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { textColor, THEME_ALU, THEME_BLACK, THEME_PRIMARY } from '../../../styles/basics/colors';
import RoutingUtils from '../../../shared/utils/utils-routing';
import { GenreOut } from '../../../store/apiLibx';
import { useTranslation } from 'react-i18next';
import { getGenreId, getSubGenreId } from '../../../shared/common/translationMappers/genreIdMapper';

const StSidebarItem = styled.li<{ active: boolean }>`
    line-height: 18px;

    & a {
        ${props => (props.active ? textColor(THEME_PRIMARY) : textColor(THEME_BLACK))};

        :hover {
            ${textColor(THEME_PRIMARY)};
        }
    }
`;

const StSidebarSubItem = styled(StSidebarItem)`
    padding-left: 15px;
`;

const StDocCount = styled.span`
    ${textColor(THEME_ALU)};
    font-size: 10px;
    margin-left: 0.3em;
`;

const StSidebar = styled.ul`
    width: 300px;
`;

const StGenreHeader = styled.div<{ marginTop?: string }>`
    font-size: 16px;
    margin-bottom: 8px;
    margin-top: ${props => props.marginTop};
`;

const SidebarItem = props => {
    const { t } = useTranslation();
    return (
        <StSidebarItem active={props.active}>
            <Link to={RoutingUtils.libxGenrePageLocationDescriptor(props.id)}>
                {props.name}
                <StDocCount>({t('numberFormat.numberWithoutDecimalPoint', { val: props.docCount })})</StDocCount>
            </Link>
        </StSidebarItem>
    );
};

SidebarItem.propTypes = {
    active: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    docCount: PropTypes.number.isRequired
};

const SidebarSubItem = props => {
    const { t } = useTranslation();
    return (
        <StSidebarSubItem active={props.active}>
            <Link to={RoutingUtils.libxGenrePageLocationDescriptor(props.parentId, props.id)}>
                {props.name}
                <StDocCount>({t('numberFormat.numberWithoutDecimalPoint', { val: props.docCount })})</StDocCount>
            </Link>
        </StSidebarSubItem>
    );
};

SidebarSubItem.propTypes = {
    ...SidebarItem.propTypes,
    parentId: PropTypes.string.isRequired
};

interface Props {
    localContentGenres: GenreOut[];
    standardGenres: GenreOut[];
    selectedGenre: string;
}

export const LibxGenresSidebar = (props: Props) => {
    const { t } = useTranslation();
    return (
        <StSidebar data-displayname="LibxGenreSidebar">
            <StGenreHeader>{t('shop.libx.localContentGenres')}</StGenreHeader>
            {flatMapDeep(props.localContentGenres, genre => [
                <SidebarItem
                    key={genre.id}
                    id={genre.id}
                    name={t(`filters:genre.${getGenreId(genre.id)}`, {
                        defaultValue: genre.name
                    })}
                    docCount={genre.docCount}
                    active={genre.id === props.selectedGenre}
                />,
                (genre.subGenres && props.selectedGenre.split('->')[0] === genre.id ? genre.subGenres : []).map(
                    subGenre => (
                        <SidebarSubItem
                            key={subGenre.id}
                            id={subGenre.id}
                            parentId={genre.id}
                            name={t(`filters:genre.${getSubGenreId(subGenre.id)}`, {
                                defaultValue: subGenre.name
                            })}
                            docCount={subGenre.docCount}
                            active={subGenre.id === props.selectedGenre}
                        />
                    )
                )
            ])}

            <StGenreHeader marginTop="25px">{t('shop.libx.borrowBoxStandardGenres')}</StGenreHeader>
            {flatMapDeep(props.standardGenres, genre => [
                <SidebarItem
                    key={genre.id}
                    id={genre.id}
                    name={t(`filters:genre.${getGenreId(genre.id)}`, {
                        defaultValue: genre.name
                    })}
                    docCount={genre.docCount}
                    active={genre.id === props.selectedGenre}
                />,
                (genre.subGenres && props.selectedGenre.split('->')[0] === genre.id ? genre.subGenres : []).map(
                    subGenre => (
                        <SidebarSubItem
                            key={subGenre.id}
                            id={subGenre.id}
                            parentId={genre.id}
                            name={t(`filters:genre.${getSubGenreId(subGenre.id)}`, {
                                defaultValue: subGenre.name
                            })}
                            docCount={subGenre.docCount}
                            active={subGenre.id === props.selectedGenre}
                        />
                    )
                )
            ])}
        </StSidebar>
    );
};
